import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, type Control } from 'react-hook-form';
import { z } from 'zod';
import { Clock, Eye, Plus, RefreshCw, Send, X } from 'lucide-react';
import {
  apiClient,
  type AdminPlan,
  type AdminCreatePlanInput,
  type AdminEmailTemplateSampleInput,
  type AdminUpdatePlanInput,
  type ApiError,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { bytesToGb, formatQuota, gbToBytes } from '@/lib/format-bytes';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  const apiErr = err as ApiError | undefined;
  if (apiErr?.message) return apiErr.message;
  return 'Erro inesperado. Tente novamente.';
}

const priceFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

function formatPriceCents(cents: number): string {
  return priceFormatter.format(cents / 100);
}

const INTERVAL_LABEL: Record<string, string> = {
  month: '/mês',
  year: '/ano',
};

/** Espelha FIRST_ACCESS_EMAIL_TOKENS (apps/api/src/common/first-access-email-template.ts). */
const EMAIL_TOKENS = [
  '{{nome}}',
  '{{email}}',
  '{{senha}}',
  '{{link}}',
  '{{plano}}',
  '{{preco}}',
  '{{trialDias}}',
  '{{escritorio}}',
];

// ---------------------------------------------------------------------------
// Painel de criação/edição
// ---------------------------------------------------------------------------

const planFormSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Código obrigatório')
    .max(64, 'Código muito longo')
    .regex(/^[a-z0-9-]+$/, 'Use apenas letras minúsculas, números e hífen'),
  name: z.string().trim().min(1, 'Nome obrigatório').max(120, 'Nome muito longo'),
  description: z.string().trim().max(2000, 'Descrição muito longa').optional(),
  // Preço em reais no form (nao em centavos) — convertido no submit. Usar
  // input type="number" evita a ambiguidade de vírgula/ponto do formato BR.
  priceReais: z
    .number({ invalid_type_error: 'Preço inválido' })
    .min(0, 'Preço não pode ser negativo'),
  currency: z.string().trim().toLowerCase().length(3, 'Moeda deve ter 3 letras'),
  interval: z.enum(['month', 'year']),
  trialDays: z
    .number({ invalid_type_error: 'Dias de trial inválido' })
    .int('Dias de trial deve ser inteiro')
    .min(0, 'Dias de trial não pode ser negativo')
    .max(365, 'Máximo 365 dias'),
  active: z.boolean(),
  sortOrder: z.number({ invalid_type_error: 'Ordem inválida' }).int('Ordem deve ser inteiro'),
  firstAccessEmailHtml: z.string().optional(),
  // Espaco de armazenamento do plano, em GB (aceita decimal, ex: 0,5).
  // undefined = campo vazio = sem limite definido no plano (null no backend).
  storageQuotaGb: z
    .number({ invalid_type_error: 'Espaço inválido' })
    .min(0, 'Espaço não pode ser negativo')
    .optional(),
});

type PlanFormValues = z.infer<typeof planFormSchema>;

function planToFormValues(plan: AdminPlan): PlanFormValues {
  return {
    code: plan.code,
    name: plan.name,
    description: plan.description ?? '',
    priceReais: plan.priceCents / 100,
    currency: plan.currency,
    interval: plan.interval === 'year' ? 'year' : 'month',
    trialDays: plan.trialDays,
    active: plan.active,
    sortOrder: plan.sortOrder,
    firstAccessEmailHtml: plan.firstAccessEmailHtml ?? '',
    storageQuotaGb:
      plan.storageQuotaBytes !== null ? bytesToGb(plan.storageQuotaBytes) : undefined,
  };
}

const EMPTY_FORM_VALUES: PlanFormValues = {
  code: '',
  name: '',
  description: '',
  priceReais: 0,
  currency: 'brl',
  interval: 'month',
  trialDays: 0,
  active: true,
  sortOrder: 0,
  firstAccessEmailHtml: '',
  storageQuotaGb: undefined,
};

/** Coage input number vazio para `undefined` em vez de NaN (mesmo padrão de cupons.tsx). */
function toOptionalNumber(value: string): number | undefined {
  return value === '' ? undefined : Number(value);
}

/** Toggle reutilizavel (mesmo padrao de admin/integracoes.tsx). */
function ToggleField({
  control,
  name,
  label,
  hint,
}: {
  control: Control<PlanFormValues>;
  name: 'active';
  label: (checked: boolean) => string;
  hint?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="flex items-start gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={field.value}
            onClick={() => field.onChange(!field.value)}
            className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 focus:ring-offset-neutral-900 ${
              field.value ? 'bg-violet-600' : 'bg-neutral-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                field.value ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <div>
            <span className="text-sm text-neutral-300">{label(field.value)}</span>
            {hint && <p className="text-xs text-neutral-500">{hint}</p>}
          </div>
        </div>
      )}
    />
  );
}

/** Monta o body comum de preview/teste a partir do snapshot atual do form. */
function buildEmailSamplePayload(values: PlanFormValues): AdminEmailTemplateSampleInput {
  return {
    html: values.firstAccessEmailHtml?.trim() ? values.firstAccessEmailHtml : undefined,
    name: values.name?.trim() ? values.name : undefined,
    // priceReais vazio no input number vira NaN; NaN é falsy -> cai em 0.
    priceCents: Math.round((values.priceReais || 0) * 100),
    trialDays: values.trialDays,
  };
}

/** Modal de pré-visualização — iframe sandboxed isola o HTML (sem scripts/forms/etc). */
function EmailPreviewModal({ html, onClose }: { html: string; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-neutral-800 px-4 py-3">
          <h3 className="text-sm font-semibold text-neutral-100">
            Pré-visualização — dados de exemplo
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <iframe
          title="Pré-visualização do e-mail"
          sandbox=""
          srcDoc={html}
          className="min-h-0 flex-1 bg-white"
        />
      </div>
    </div>
  );
}

interface PlanFormPanelProps {
  /** null = criando um plano novo. */
  plan: AdminPlan | null;
  onClose: () => void;
  onSaved: () => void;
}

function PlanFormPanel({ plan, onClose, onSaved }: PlanFormPanelProps) {
  const isEditing = plan !== null;
  const currentUserEmail = useAuthStore((s) => s.user?.email);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<PlanFormValues>({
    resolver: zodResolver(planFormSchema),
    defaultValues: plan ? planToFormValues(plan) : EMPTY_FORM_VALUES,
  });

  const templateMutation = useMutation({
    mutationFn: () => apiClient.adminGetDefaultEmailTemplate(),
    onSuccess: (data) => {
      const current = getValues('firstAccessEmailHtml');
      if (
        current &&
        current.trim() !== '' &&
        !window.confirm('Isso substitui o HTML atual do campo. Continuar?')
      ) {
        return;
      }
      setValue('firstAccessEmailHtml', data.html, { shouldDirty: true });
    },
  });

  // Visualizar / Enviar teste — usam o HTML ATUAL do textarea (getValues),
  // funcionam mesmo antes de salvar o plano.
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    if (currentUserEmail) {
      setTestEmail((prev) => prev || currentUserEmail);
    }
  }, [currentUserEmail]);

  const previewMutation = useMutation({
    mutationFn: () => apiClient.adminPreviewEmailTemplate(buildEmailSamplePayload(getValues())),
    onSuccess: (data) => setPreviewHtml(data.html),
  });

  const testMutation = useMutation({
    mutationFn: () =>
      apiClient.adminTestEmailTemplate({
        ...buildEmailSamplePayload(getValues()),
        email: testEmail.trim(),
      }),
  });

  const saveMutation = useMutation({
    mutationFn: (values: PlanFormValues) => {
      const priceCents = Math.round(values.priceReais * 100);
      const description = values.description?.trim() ? values.description.trim() : null;
      const firstAccessEmailHtml = values.firstAccessEmailHtml?.trim()
        ? values.firstAccessEmailHtml
        : null;
      // Vazio no form = sem limite definido no plano.
      const storageQuotaBytes =
        values.storageQuotaGb !== undefined ? gbToBytes(values.storageQuotaGb) : null;

      if (plan) {
        const input: AdminUpdatePlanInput = {
          name: values.name,
          description,
          priceCents,
          currency: values.currency,
          interval: values.interval,
          trialDays: values.trialDays,
          active: values.active,
          sortOrder: values.sortOrder,
          firstAccessEmailHtml,
          storageQuotaBytes,
        };
        return apiClient.adminUpdatePlan(plan.id, input);
      }

      const input: AdminCreatePlanInput = {
        code: values.code,
        name: values.name,
        description,
        priceCents,
        currency: values.currency,
        interval: values.interval,
        trialDays: values.trialDays,
        active: values.active,
        sortOrder: values.sortOrder,
        firstAccessEmailHtml,
        storageQuotaBytes,
      };
      return apiClient.adminCreatePlan(input);
    },
    onSuccess: () => onSaved(),
  });

  function onSubmit(values: PlanFormValues) {
    saveMutation.mutate(values);
  }

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-neutral-100">
            {isEditing ? `Editar plano — ${plan.name}` : 'Novo plano'}
          </h2>
          <p className="mt-0.5 text-sm text-neutral-400">
            {isEditing
              ? 'O código do plano não pode ser alterado depois de criado.'
              : 'O código não pode ser alterado depois de criado — escolha com cuidado.'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="plan-code" className="block text-sm font-medium text-neutral-300">
              Código
            </label>
            <input
              id="plan-code"
              type="text"
              {...register('code')}
              disabled={isEditing}
              placeholder="ex: profissional"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {isEditing ? (
              <p className="text-xs text-neutral-500">
                Identificador estável usado no checkout. Somente leitura.
              </p>
            ) : (
              <p className="text-xs text-neutral-500">kebab-case: letras minúsculas, números e hífen.</p>
            )}
            {errors.code && <p className="text-xs text-red-400">{errors.code.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-name" className="block text-sm font-medium text-neutral-300">
              Nome
            </label>
            <input
              id="plan-name"
              type="text"
              {...register('name')}
              placeholder="ex: Profissional"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.name && <p className="text-xs text-red-400">{errors.name.message}</p>}
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="plan-description" className="block text-sm font-medium text-neutral-300">
            Descrição
          </label>
          <textarea
            id="plan-description"
            {...register('description')}
            rows={2}
            placeholder="Descrição curta exibida no checkout (opcional)"
            className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
          />
          {errors.description && (
            <p className="text-xs text-red-400">{errors.description.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="space-y-1.5">
            <label htmlFor="plan-price" className="block text-sm font-medium text-neutral-300">
              Preço (R$)
            </label>
            <input
              id="plan-price"
              type="number"
              step="0.01"
              min="0"
              {...register('priceReais', { valueAsNumber: true })}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.priceReais && (
              <p className="text-xs text-red-400">{errors.priceReais.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-currency" className="block text-sm font-medium text-neutral-300">
              Moeda
            </label>
            <select
              id="plan-currency"
              {...register('currency')}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            >
              <option value="brl">BRL</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-interval" className="block text-sm font-medium text-neutral-300">
              Intervalo
            </label>
            <select
              id="plan-interval"
              {...register('interval')}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            >
              <option value="month">Mensal</option>
              <option value="year">Anual</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="plan-trial" className="block text-sm font-medium text-neutral-300">
              Trial (dias)
            </label>
            <input
              id="plan-trial"
              type="number"
              step="1"
              min="0"
              max="365"
              {...register('trialDays', { valueAsNumber: true })}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.trialDays && (
              <p className="text-xs text-red-400">{errors.trialDays.message}</p>
            )}
          </div>
        </div>

        {isEditing && (
          <p className="rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
            Assinantes atuais mantêm o preço antigo; só novos checkouts usam o novo valor, e
            somente depois de clicar em "Sincronizar com Stripe" na lista.
          </p>
        )}

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="space-y-1.5">
            <label htmlFor="plan-sort-order" className="block text-sm font-medium text-neutral-300">
              Ordem
            </label>
            <input
              id="plan-sort-order"
              type="number"
              step="1"
              {...register('sortOrder', { valueAsNumber: true })}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.sortOrder && (
              <p className="text-xs text-red-400">{errors.sortOrder.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="plan-storage-quota"
              className="block text-sm font-medium text-neutral-300"
            >
              Espaço de armazenamento (GB)
            </label>
            <input
              id="plan-storage-quota"
              type="number"
              step="0.1"
              min="0"
              placeholder="Sem limite"
              {...register('storageQuotaGb', { setValueAs: toOptionalNumber })}
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
            {errors.storageQuotaGb && (
              <p className="text-xs text-red-400">{errors.storageQuotaGb.message}</p>
            )}
          </div>

          <div className="col-span-2 flex items-end pb-2">
            <ToggleField
              control={control}
              name="active"
              label={(checked) => `Plano ${checked ? 'ativo' : 'inativo'}`}
              hint="Planos inativos não aparecem no checkout."
            />
          </div>
        </div>

        <div className="space-y-1.5 border-t border-neutral-800 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label htmlFor="plan-email-html" className="block text-sm font-medium text-neutral-300">
              E-mail de primeiro acesso (HTML customizado)
            </label>
            <button
              type="button"
              onClick={() => templateMutation.mutate()}
              disabled={templateMutation.isPending}
              className="shrink-0 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs font-medium text-neutral-200 hover:bg-neutral-700 disabled:opacity-50"
            >
              {templateMutation.isPending ? 'Carregando...' : 'Carregar template padrão'}
            </button>
          </div>
          <textarea
            id="plan-email-html"
            {...register('firstAccessEmailHtml')}
            rows={14}
            placeholder="Vazio = usa o e-mail padrão do sistema"
            spellCheck={false}
            className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 font-mono text-xs text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
          />
          <p className="text-xs text-neutral-500">
            Deixe em branco para usar o e-mail padrão do sistema. Variáveis disponíveis
            (substituídas automaticamente no envio):
          </p>
          <div className="flex flex-wrap gap-1.5">
            {EMAIL_TOKENS.map((token) => (
              <code
                key={token}
                className="rounded bg-neutral-800 px-1.5 py-0.5 font-mono text-xs text-neutral-300"
              >
                {token}
              </code>
            ))}
          </div>
          {templateMutation.isError && (
            <p className="text-xs text-red-400">{getErrorMessage(templateMutation.error)}</p>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-neutral-800 pt-3">
            <button
              type="button"
              onClick={() => previewMutation.mutate()}
              disabled={previewMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs font-medium text-neutral-200 hover:bg-neutral-700 disabled:opacity-50"
            >
              <Eye className="h-3.5 w-3.5" />
              {previewMutation.isPending ? 'Gerando...' : 'Visualizar'}
            </button>

            <div className="flex items-center gap-1.5">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="destino@exemplo.com"
                aria-label="E-mail de destino do teste"
                className="w-52 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
              />
              <button
                type="button"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending || !testEmail.trim()}
                className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs font-medium text-neutral-200 hover:bg-neutral-700 disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
                {testMutation.isPending ? 'Enviando...' : 'Enviar teste'}
              </button>
            </div>
          </div>

          {previewMutation.isError && (
            <p className="text-xs text-red-400">{getErrorMessage(previewMutation.error)}</p>
          )}
          {testMutation.isError && (
            <p className="text-xs text-red-400">{getErrorMessage(testMutation.error)}</p>
          )}
          {testMutation.isSuccess && (
            <p className="text-xs text-green-400">
              E-mail de teste enviado para {testEmail.trim()}.
            </p>
          )}
        </div>

        {saveMutation.isError && (
          <p className="text-sm text-red-400">{getErrorMessage(saveMutation.error)}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-4 py-2 text-sm font-medium text-neutral-300 hover:bg-neutral-800"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
          >
            {saveMutation.isPending
              ? 'Salvando...'
              : isEditing
                ? 'Salvar alterações'
                : 'Criar plano'}
          </button>
        </div>
      </form>

      {previewHtml !== null && (
        <EmailPreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Política de cobrança (inadimplência) — config global em platform_settings,
// mesmo par GET/PATCH que a página de Aparência já usa.
// ---------------------------------------------------------------------------

interface PastDueGraceSectionProps {
  /** Valor atual carregado do GET — usado só como valor inicial do input. */
  initialGraceDays: number;
}

function PastDueGraceSection({ initialGraceDays }: PastDueGraceSectionProps) {
  const queryClient = useQueryClient();
  const [graceDays, setGraceDays] = useState(initialGraceDays);

  const saveMutation = useMutation({
    mutationFn: () => apiClient.adminPatchPlatformSettings({ pastDueGraceDays: graceDays }),
    onSuccess: (data) => {
      setGraceDays(data.pastDueGraceDays);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'platform-settings'] });
    },
  });

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-neutral-300">
          <Clock className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-neutral-100">Política de cobrança</h2>
          <p className="mt-0.5 text-sm text-neutral-400">
            Configuração global de bloqueio por inadimplência, aplicada a todos os escritórios.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <label htmlFor="past-due-grace-days" className="block text-sm font-medium text-neutral-300">
            Dias de tolerância antes de bloquear por falta de pagamento
          </label>
          <input
            id="past-due-grace-days"
            type="number"
            min={0}
            max={90}
            step={1}
            value={graceDays}
            onChange={(e) => setGraceDays(Number(e.target.value))}
            className="w-32 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
          />
        </div>
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-neutral-200 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      <p className="mt-2 text-xs text-neutral-500">
        Depois desse número de dias em atraso, o escritório é bloqueado até regularizar a
        assinatura. 0 bloqueia assim que o pagamento falha.
      </p>

      {saveMutation.isError && (
        <p className="mt-2 text-xs text-red-400">{getErrorMessage(saveMutation.error)}</p>
      )}
      {saveMutation.isSuccess && (
        <p className="mt-2 text-xs text-green-400">Configuração salva.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
      <div className="divide-y divide-neutral-800">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-4 w-32 animate-pulse rounded bg-neutral-800" />
            <div className="h-4 w-20 animate-pulse rounded bg-neutral-800" />
            <div className="h-4 w-16 animate-pulse rounded bg-neutral-800" />
            <div className="ml-auto h-4 w-24 animate-pulse rounded bg-neutral-800" />
          </div>
        ))}
      </div>
    </div>
  );
}

type PanelState = 'closed' | 'new' | AdminPlan;

export default function AdminPlanosPage() {
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<PanelState>('closed');

  const {
    data: plans,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: () => apiClient.adminListPlans(),
  });

  // Mesma queryKey da página de Aparência — cache compartilhado entre as duas.
  const { data: platformSettings } = useQuery({
    queryKey: ['admin', 'platform-settings'],
    queryFn: () => apiClient.adminGetPlatformSettings(),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
  }

  const syncMutation = useMutation({
    mutationFn: (id: string) => apiClient.adminSyncPlan(id),
    onSuccess: invalidate,
    onError: (err) => window.alert(getErrorMessage(err)),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.adminDeletePlan(id),
    onSuccess: invalidate,
    onError: (err) => window.alert(getErrorMessage(err)),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.adminUpdatePlan(id, { active: true }),
    onSuccess: invalidate,
    onError: (err) => window.alert(getErrorMessage(err)),
  });

  const isBusy =
    syncMutation.isPending || deactivateMutation.isPending || reactivateMutation.isPending;

  function handleSync(plan: AdminPlan) {
    if (
      !window.confirm(
        `Sincronizar "${plan.name}" com o Stripe? Isso cria um Price novo${
          plan.stripePriceId ? ' e arquiva o Price atual (assinantes existentes não são afetados)' : ''
        }.`,
      )
    )
      return;
    syncMutation.mutate(plan.id);
  }

  function handleToggleActive(plan: AdminPlan) {
    if (plan.active) {
      if (
        !window.confirm(`Desativar o plano "${plan.name}"? Ele deixa de aparecer no checkout.`)
      )
        return;
      deactivateMutation.mutate(plan.id);
    } else {
      reactivateMutation.mutate(plan.id);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-100">Planos</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Catálogo de planos de assinatura, sincronização com Stripe e e-mail de primeiro
            acesso por plano.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPanel('new')}
          className="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-900 hover:bg-neutral-200"
        >
          <Plus className="h-4 w-4" />
          Novo plano
        </button>
      </header>

      {platformSettings && (
        <PastDueGraceSection initialGraceDays={platformSettings.pastDueGraceDays} />
      )}

      {isPending ? (
        <LoadingSkeleton />
      ) : isError ? (
        <div className="rounded-lg border border-red-900/50 bg-red-950/30 p-4 text-sm text-red-200">
          Não foi possível carregar os planos agora.
          {error instanceof Error && error.message ? (
            <span className="ml-1 opacity-70">({error.message})</span>
          ) : null}
        </div>
      ) : !plans || plans.length === 0 ? (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center">
          <p className="text-sm text-neutral-400">Nenhum plano cadastrado ainda.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-950/40 text-left text-xs font-medium uppercase tracking-wide text-neutral-400">
              <tr>
                <th className="px-4 py-3">Plano</th>
                <th className="px-4 py-3">Preço</th>
                <th className="px-4 py-3">Trial</th>
                <th className="px-4 py-3">Armazenamento</th>
                <th className="px-4 py-3">Stripe</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-neutral-800/40">
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-100">{plan.name}</div>
                    <div className="font-mono text-xs text-neutral-500">{plan.code}</div>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">
                    {formatPriceCents(plan.priceCents)}
                    <span className="text-neutral-500">{INTERVAL_LABEL[plan.interval] ?? ''}</span>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">
                    {plan.trialDays > 0 ? `${plan.trialDays} dias` : '—'}
                  </td>
                  <td className="px-4 py-3 text-neutral-300">
                    {formatQuota(plan.storageQuotaBytes)}
                  </td>
                  <td className="px-4 py-3">
                    {plan.stripePriceId ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-900/40 px-2 py-0.5 text-xs font-medium text-emerald-200">
                        Sincronizado
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-neutral-800 px-2 py-0.5 text-xs font-medium text-neutral-400">
                        Pendente
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        plan.active
                          ? 'bg-emerald-900/40 text-emerald-200'
                          : 'bg-neutral-800 text-neutral-400'
                      }`}
                    >
                      {plan.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleSync(plan)}
                        disabled={isBusy}
                        title="Sincronizar com Stripe"
                        className="inline-flex items-center gap-1 rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-medium text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Sincronizar
                      </button>
                      <button
                        type="button"
                        onClick={() => setPanel(plan)}
                        className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-medium text-neutral-300 hover:bg-neutral-800"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleActive(plan)}
                        disabled={isBusy}
                        className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-medium text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
                      >
                        {plan.active ? 'Desativar' : 'Reativar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {panel !== 'closed' && (
        <PlanFormPanel
          key={panel === 'new' ? 'new' : panel.id}
          plan={panel === 'new' ? null : panel}
          onClose={() => setPanel('closed')}
          onSaved={() => {
            setPanel('closed');
            invalidate();
          }}
        />
      )}
    </div>
  );
}
